import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star } from "lucide-react";
import { motion } from "framer-motion";

export default function ReviewsCarousel() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  if (!reviews.length) return null;

  return (
    <section data-testid="reviews-section" className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Google Reviews</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight">
            Loved by <span className="italic gold-text">island drivers</span>
          </h2>
          <p className="mt-2 text-sm font-ui text-muted-foreground flex items-center gap-1.5">
            <Star size={14} className="fill-amber-400 text-amber-400" /> 5.0 on Google — every review shown is from our verified Google Business listing
          </p>
        </div>
        <a data-testid="reviews-google-link" href="https://www.google.com/maps/place/Easy+Drive+Car+Rental/data=!4m7!3m6!1s0x9e3edcf257ec0eb:0xe8df56bc3ef24b7b" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white border border-border rounded-full px-5 py-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
          <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
            <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.5-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
          </svg>
          <span className="font-ui text-sm font-semibold text-foreground/80">Read all reviews on Google</span>
        </a>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.slice(0, 6).map((r, i) => (
          <motion.figure
            key={r.id}
            data-testid={`review-card-${r.id}`}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="bg-white border border-border rounded-3xl p-7 flex flex-col shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
          >
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={15} className={s <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/25"} />
              ))}
            </div>
            <blockquote className="mt-4 text-sm leading-relaxed text-foreground/80 flex-1">"{r.text}"</blockquote>
            <figcaption className="mt-5 pt-4 border-t border-border flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-ui font-semibold text-sm shrink-0">
                {r.name.charAt(0)}
              </span>
              <div>
                <p className="font-display text-base tracking-tight">{r.name}</p>
                {r.vehicle_name && <p className="text-[11px] text-muted-foreground font-ui mt-0.5">Rented the {r.vehicle_name}</p>}
                {r.google_maps_uri && (
                  <a data-testid={`review-google-link-${r.id}`} href={r.google_maps_uri} target="_blank" rel="noopener noreferrer" className="text-[11px] font-ui text-primary hover:underline mt-0.5 inline-block">
                    Google review — view on Maps
                  </a>
                )}
              </div>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}
